/**
 * AiPanel.js — DOM management for the AI assistant panel.
 * Handles open/close, rendering messages, chips, context pill, history, and chat tabs.
 */

// Storage keys are scoped per diagram via _tabsKey() / _histKey() instance methods.

// ── Rotating loading messages per intent ─────────────────────────────────────
// Each array is cycled every ~2.5 s while the AI is working.

const LOADING_MESSAGES = {
  validate: [
    'Analizando el diagrama…',
    'Verificando la estructura BPMN…',
    'Revisando conexiones y compuertas…',
    'Buscando posibles problemas…',
    'Comprobando flujos de secuencia…',
    'Casi listo…',
  ],
  explain: [
    'Leyendo el diagrama…',
    'Interpretando el flujo…',
    'Preparando la explicación…',
    'Analizando los pasos del proceso…',
    'Generando descripción…',
  ],
  document: [
    'Recopilando información del proceso…',
    'Redactando la documentación…',
    'Estructurando el contenido…',
    'Aplicando formato…',
    'Casi listo…',
  ],
  optimize: [
    'Analizando el proceso…',
    'Identificando cuellos de botella…',
    'Evaluando oportunidades de mejora…',
    'Formulando recomendaciones…',
    'Casi listo…',
  ],
  generate: [
    'Interpretando la descripción…',
    'Diseñando la estructura del proceso…',
    'Creando elementos BPMN…',
    'Conectando los flujos…',
    'Ajustando el diagrama…',
    'Casi listo…',
  ],
  // ── DMN / rules mode ─────────────────────────────────────────────────────
  dmn_generate: [
    'Interpretando la lógica de negocio…',
    'Definiendo entradas y salidas…',
    'Creando las reglas de decisión…',
    'Evaluando condiciones FEEL…',
    'Generando la tabla DMN…',
    'Casi listo…',
  ],
  dmn_validate: [
    'Analizando la tabla de decisión…',
    'Verificando reglas conflictivas…',
    'Revisando cobertura de casos…',
    'Comprobando expresiones FEEL…',
    'Casi listo…',
  ],
  dmn_explain: [
    'Leyendo la tabla de decisión…',
    'Interpretando las reglas…',
    'Preparando la explicación…',
    'Casi listo…',
  ],
  dmn_modify: [
    'Analizando el cambio solicitado…',
    'Actualizando las reglas…',
    'Casi listo…',
  ],
  default: [
    'Pensando…',
    'Procesando tu consulta…',
    'Analizando el contexto…',
    'Preparando la respuesta…',
    'Casi listo…',
  ],
};

// Messages used in the modal's loading step (structure + generate phases)
export const MODAL_LOADING_MESSAGES = {
  structure: [
    'Interpretando la descripción…',
    'Identificando actores y tareas…',
    'Estructurando el proceso…',
    'Organizando los pasos…',
    'Casi listo…',
  ],
  generate: [
    'Diseñando la estructura BPMN…',
    'Creando compuertas y eventos…',
    'Conectando los flujos de secuencia…',
    'Ajustando la topología del diagrama…',
    'Generando XML BPMN…',
    'Validando el resultado…',
    'Casi listo…',
  ],
};

// ── Minimal Markdown renderer ─────────────────────────────────────────────────
// Handles: **bold**, `code`, - / * bullet lists, and 1. numbered lists.

function _applyInlineMarkdown(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`(.+?)`/g, '<code>$1</code>');
}

function renderMarkdown(text) {
  // Escape HTML entities first to prevent XSS
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const lines = escaped.split('\n');
  let html = '';
  let listType = null; // 'ul' | 'ol' | null

  const closeList = () => {
    if (listType) { html += `</${listType}>`; listType = null; }
  };

  for (const line of lines) {
    if (/^\s*[-*]\s+/.test(line)) {
      if (listType !== 'ul') { closeList(); html += '<ul class="ai-md-list">'; listType = 'ul'; }
      html += `<li>${_applyInlineMarkdown(line.replace(/^\s*[-*]\s+/, ''))}</li>`;
    } else if (/^\s*\d+\.\s+/.test(line)) {
      if (listType !== 'ol') { closeList(); html += '<ol class="ai-md-list">'; listType = 'ol'; }
      html += `<li>${_applyInlineMarkdown(line.replace(/^\s*\d+\.\s+/, ''))}</li>`;
    } else {
      closeList();
      if (line.trim() === '') {
        html += '<br>';
      } else {
        html += `<p>${_applyInlineMarkdown(line)}</p>`;
      }
    }
  }
  closeList();
  return html;
}

function _fmtTime() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function _buildAvatar() {
  const el = document.createElement('div');
  el.className = 'ai-msg__avatar';
  el.textContent = '✦';
  return el;
}

function _buildBody(...children) {
  const el = document.createElement('div');
  el.className = 'ai-msg__body';
  for (const child of children) el.appendChild(child);
  return el;
}

function _buildTime() {
  const el = document.createElement('span');
  el.className = 'ai-msg__time';
  el.textContent = _fmtTime();
  return el;
}

function makeTabId() {
  return 'chat_' + Date.now();
}

const DMN_TYPE_LABEL = {
  'dmn:Decision':                  'Decisión',
  'dmn:InputData':                 'Input',
  'dmn:BusinessKnowledgeModel':    'BKM',
  'dmn:KnowledgeSource':           'Fuente',
  'dmn:DecisionService':           'Servicio',
  'dmn:Definitions':               'DRD',
};

function _shortDmnType(typeStr) {
  if (!typeStr) return '';
  if (DMN_TYPE_LABEL[typeStr]) return DMN_TYPE_LABEL[typeStr];
  return typeStr.split(':').pop().replace(/([A-Z])/g, ' $1').trim();
}

export class AiPanel {
  constructor() {
    this._panel     = document.getElementById('ai-panel');
    this._fab       = document.getElementById('ai-fab');
    this._messages  = document.getElementById('ai-messages');
    this._chips     = document.getElementById('ai-chips');
    this._ctxLabel  = document.getElementById('ai-context-label');
    this._ctxPill   = document.getElementById('ai-context-pill');
    this._selList   = document.getElementById('ai-selection-list');
    this._clearBtn  = document.getElementById('ai-btn-clear');
    this._tabsEl    = document.getElementById('ai-tabs');

    // ── Diagram scoping (set via switchDiagram; 'default' on first load) ──
    this._diagramId = 'default';

    // ── DMN scoping (PR 1 §4.6) ───────────────────────────────────────────
    // When non-null the chat is scoped to a specific decision inside the
    // active diagram's DMN, so its tabs and history live under a separate
    // bucket. Mode is tracked alongside so tabs/history don't collide between
    // BPMN and DMN views of the same diagram id.
    this._mode        = 'bpmn';
    this._decisionRef = null;

    // ── Tab system ────────────────────────────────────────────────────────
    this._tabs = this._loadTabIndex();
    if (!this._tabs.length) {
      this._tabs = [{ id: makeTabId(), name: 'Chat 1' }];
      this._saveTabIndex();
    }
    this._activeTabId = this._tabs[0].id;
    this._renderTabs();

    this._clearBtn?.addEventListener('click', () => this.clearHistory());
  }

  // ── Diagram-scoped storage keys ───────────────────────────────────────────
  //
  // PR 1 §4.6 — when the chat is scoped to a DMN decision the key suffix
  // becomes `__dmn_<decisionRef>` so each decision keeps its own tabs and
  // history. Falls back to the diagram-only key for BPMN mode and for the
  // DMN root view.

  _scopeSuffix() {
    if (this._mode === 'rules' && this._decisionRef) {
      return '__dmn_' + this._decisionRef;
    }
    return '';
  }

  _tabsKey()      { return 'atlas_tabs_'  + this._diagramId + this._scopeSuffix(); }
  _histKey(tabId) { return 'atlas_hist_'  + this._diagramId + this._scopeSuffix() + '_' + tabId; }

  // ── Open / Close ─────────────────────────────────────────────────────────

  open() {
    this._panel.classList.add('is-open');
    this._fab.classList.add('is-hidden');
    document.body.classList.add('atlas-chat-open');
  }

  close() {
    this._panel.classList.remove('is-open');
    this._fab.classList.remove('is-hidden');
    document.body.classList.remove('atlas-chat-open');
  }

  // ── Tab management ────────────────────────────────────────────────────────

  _loadTabIndex() {
    try {
      const raw = localStorage.getItem(this._tabsKey());
      return raw ? JSON.parse(raw) : [];
    } catch (_) { return []; }
  }

  _saveTabIndex() {
    try { localStorage.setItem(this._tabsKey(), JSON.stringify(this._tabs)); } catch (_) {}
  }

  _renderTabs() {
    if (!this._tabsEl) return;
    this._tabsEl.innerHTML = '';

    for (const tab of this._tabs) {
      const tabEl = document.createElement('button');
      tabEl.className = 'ai-tab' + (tab.id === this._activeTabId ? ' is-active' : '');

      const label = document.createElement('span');
      label.className = 'ai-tab__label';
      label.textContent = tab.name;
      tabEl.appendChild(label);

      if (this._tabs.length > 1) {
        const closeBtn = document.createElement('span');
        closeBtn.className = 'ai-tab__close';
        closeBtn.textContent = '×';
        closeBtn.title = 'Cerrar chat';
        closeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._closeTab(tab.id);
        });
        tabEl.appendChild(closeBtn);
      }

      tabEl.addEventListener('click', () => {
        if (tab.id !== this._activeTabId) this._switchTab(tab.id);
      });

      this._tabsEl.appendChild(tabEl);
    }

    // "+" new chat button
    const newBtn = document.createElement('button');
    newBtn.className = 'ai-tab ai-tab--new';
    newBtn.textContent = '+';
    newBtn.title = 'Nuevo chat';
    newBtn.addEventListener('click', () => this._newChat());
    this._tabsEl.appendChild(newBtn);
  }

  _newChat() {
    const n = this._tabs.length + 1;
    const id = makeTabId();
    this._tabs.push({ id, name: `Chat ${n}` });
    this._saveTabIndex();
    this._switchTab(id);
  }

  _switchTab(id) {
    this._activeTabId = id;
    this._messages.innerHTML = '';
    const history = this.loadHistory();
    if (history.length > 0) {
      for (const msg of history) {
        if (msg.role === 'user') this.addUserMessage(msg.content);
        else                     this.addAiMessage(msg.content);
      }
    } else {
      this._messages.appendChild(this._buildEmptyState());
    }
    this._renderTabs();
  }

  _closeTab(id) {
    if (this._tabs.length <= 1) return;
    const idx = this._tabs.findIndex(t => t.id === id);
    this._tabs = this._tabs.filter(t => t.id !== id);
    try { localStorage.removeItem(this._histKey(id)); } catch (_) {}

    if (this._activeTabId === id) {
      const newIdx = Math.min(idx, this._tabs.length - 1);
      this._activeTabId = this._tabs[newIdx].id;
      this._saveTabIndex();
      this._switchTab(this._activeTabId);
    } else {
      this._saveTabIndex();
      this._renderTabs();
    }
  }

  /**
   * Renames the active tab using the first user message (if it still has a default name).
   * @param {string} firstMessage
   */
  renameActiveTab(firstMessage) {
    const tab = this._tabs.find(t => t.id === this._activeTabId);
    if (tab && /^Chat \d+$/.test(tab.name)) {
      const trimmed = firstMessage.trim();
      tab.name = trimmed.length > 22 ? trimmed.slice(0, 21) + '…' : trimmed;
      this._saveTabIndex();
      this._renderTabs();
    }
  }

  // ── Context pill ──────────────────────────────────────────────────────────

  setContext(selection) {
    if (!selection || selection.length === 0) {
      this._ctxLabel.textContent = 'Todo el diagrama';
      this._ctxPill.className = 'ai-context-pill';
      if (this._selList) this._selList.style.display = 'none';
    } else if (selection.length === 1) {
      const el = selection[0];
      const name = el.properties?.name || el.text?.value || el.id;
      const shortType = (el.type || '').split(':').pop().replace(/([A-Z])/g, ' $1').trim();
      this._ctxLabel.textContent = `${shortType}: ${name}`;
      this._ctxPill.className = 'ai-context-pill is-selection';
      this._renderSelectionList(selection);
    } else {
      this._ctxLabel.textContent = `${selection.length} elementos`;
      this._ctxPill.className = 'ai-context-pill is-selection';
      this._renderSelectionList(selection);
    }
  }

  _renderSelectionList(selection) {
    if (!this._selList) return;
    this._selList.innerHTML = '';
    for (const el of selection) {
      const name = el.properties?.name || el.text?.value || el.id;
      const rawType = (el.type || '').split(':').pop();
      const shortType = rawType.replace(/([A-Z])/g, ' $1').trim().slice(0, 12);

      const item = document.createElement('span');
      item.className = 'ai-sel-item';
      item.title = `${rawType}: ${name}`;

      const typeEl = document.createElement('span');
      typeEl.className = 'ai-sel-item__type';
      typeEl.textContent = shortType;

      const nameEl = document.createElement('span');
      nameEl.className = 'ai-sel-item__name';
      nameEl.textContent = name;

      item.appendChild(typeEl);
      item.appendChild(nameEl);
      this._selList.appendChild(item);
    }
    this._selList.style.display = 'flex';
  }

  /**
   * Sets a fixed label on the context pill (used when in DMN / rules mode).
   * @param {string} label
   */
  setModeLabel(label) {
    this._ctxLabel.textContent = label;
    this._ctxPill.className = 'ai-context-pill is-rules';
    if (this._selList) this._selList.style.display = 'none';
  }

  /**
   * Themis context pill — mirrors the Atlas behaviour but for DMN. Reflects
   * the active decision, the current view (DRD / table / expression), and
   * the selected nodes inside the DRD. Renders the selection list so the
   * user can see exactly which elements feed the next prompt.
   *
   * @param {object} ctx
   * @param {string} [ctx.decisionName]  Display name of the active decision
   * @param {string} [ctx.decisionRef]   Decision id (fallback when name missing)
   * @param {'drd'|'table'|'expression'} [ctx.view]
   * @param {Array}  [ctx.selection]     [{ id, type, name, businessObject? }]
   */
  setRulesContext(ctx = {}) {
    const view = ctx.view || 'drd';
    const decision = ctx.decisionName || ctx.decisionRef || 'Tabla de decisión';
    const sel = Array.isArray(ctx.selection) ? ctx.selection : [];

    let label;
    if (view === 'table') {
      label = `Tabla: ${decision}`;
    } else if (view === 'expression') {
      label = `Expresión: ${decision}`;
    } else if (sel.length === 1) {
      const el = sel[0];
      const name = el.name || el.businessObject?.name || el.id;
      const type = _shortDmnType(el.type || el.businessObject?.$type);
      label = `${type}: ${name}`;
    } else if (sel.length > 1) {
      label = `${sel.length} elementos · ${decision}`;
    } else {
      label = `Regla: ${decision}`;
    }

    this._ctxLabel.textContent = label;
    this._ctxPill.className = 'ai-context-pill is-rules';
    if (sel.length > 0) {
      this._renderDmnSelectionList(sel);
    } else if (this._selList) {
      this._selList.style.display = 'none';
    }
  }

  _renderDmnSelectionList(selection) {
    if (!this._selList) return;
    this._selList.innerHTML = '';
    for (const el of selection) {
      const name = el.name || el.businessObject?.name || el.id;
      const fullType = el.type || el.businessObject?.$type || '';
      const shortType = _shortDmnType(fullType).slice(0, 14);

      const item = document.createElement('span');
      item.className = 'ai-sel-item';
      item.title = `${fullType}: ${name}`;

      const typeEl = document.createElement('span');
      typeEl.className = 'ai-sel-item__type';
      typeEl.textContent = shortType;

      const nameEl = document.createElement('span');
      nameEl.className = 'ai-sel-item__name';
      nameEl.textContent = name;

      item.appendChild(typeEl);
      item.appendChild(nameEl);
      this._selList.appendChild(item);
    }
    this._selList.style.display = 'flex';
  }

  /**
   * Sets the visual mode of the panel (none | selection | rules).
   * Controls: header stripe, include-selection checkbox, textarea placeholder.
   * @param {'none'|'selection'|'rules'} mode
   */
  setVisualMode(mode) {
    this._panel.classList.toggle('is-mode-selection', mode === 'selection');
    this._panel.classList.toggle('is-mode-rules',     mode === 'rules');

    // Show the "Incluir selección" row only when nodes are selected
    const inclRow = document.querySelector('.ai-include-label');
    if (inclRow) inclRow.hidden = (mode !== 'selection');

    // Adapt textarea placeholder
    const ta = document.getElementById('ai-input');
    if (ta) {
      const P = {
        none:      'Describí lo que querés hacer…',
        selection: '¿Qué querés hacer con la selección?',
        rules:     'Pregunta sobre la tabla de decisión…',
      };
      ta.placeholder = P[mode] ?? P.none;
    }
  }

  // ── Chips ─────────────────────────────────────────────────────────────────

  setChips(chipLabels, onChipClick) {
    this._chips.innerHTML = '';
    for (const label of chipLabels) {
      const btn = document.createElement('button');
      btn.className = 'ai-chip';
      btn.textContent = label;
      btn.addEventListener('click', () => onChipClick(label));
      this._chips.appendChild(btn);
    }
  }

  // ── Messages ──────────────────────────────────────────────────────────────

  addUserMessage(text) {
    this._hideEmpty();
    const el = this._createBubble('is-user', text);
    this._messages.appendChild(el);
    this._scrollToBottom();
    return el;
  }

  addAiMessage(text, actions = []) {
    this._hideEmpty();
    const el = this._createBubble('is-ai', text, actions);
    this._messages.appendChild(el);
    this._scrollToBottom();
    return el;
  }

  /**
   * Shows a loading bubble with rotating contextual messages.
   * @param {string} intent  - Optional intent key for context-aware messages.
   * @param {string} [label] - Deprecated static label (ignored when intent provided).
   */
  addLoadingBubble(intent = 'default', label = '') {
    this._hideEmpty();
    const wrapper = document.createElement('div');
    wrapper.className = 'ai-msg is-ai is-loading';

    const bubble = document.createElement('div');
    bubble.className = 'ai-msg__bubble';

    const messages = LOADING_MESSAGES[intent] || LOADING_MESSAGES.default;
    let msgIndex = 0;

    const lbl = document.createElement('span');
    lbl.className = 'ai-loading-label';
    lbl.textContent = label || messages[0];
    bubble.appendChild(lbl);

    const typing = document.createElement('div');
    typing.className = 'ai-typing';
    for (let i = 0; i < 3; i++) {
      const dot = document.createElement('span');
      dot.className = 'ai-typing__dot';
      typing.appendChild(dot);
    }
    bubble.appendChild(typing);

    wrapper.appendChild(_buildAvatar());
    wrapper.appendChild(_buildBody(bubble));
    this._messages.appendChild(wrapper);
    this._scrollToBottom();

    // Rotate messages unless a fixed label was explicitly provided
    if (!label) {
      wrapper._rotateInterval = setInterval(() => {
        lbl.classList.add('is-fading');
        setTimeout(() => {
          msgIndex = (msgIndex + 1) % messages.length;
          lbl.textContent = messages[msgIndex];
          lbl.classList.remove('is-fading');
        }, 300);
      }, 2500);
    }

    return wrapper;
  }

  replaceLoading(loadingEl, text, actions = []) {
    if (!loadingEl || !loadingEl.parentNode) return;
    // Stop rotation
    if (loadingEl._rotateInterval) {
      clearInterval(loadingEl._rotateInterval);
      loadingEl._rotateInterval = null;
    }
    if (text) {
      const replacement = this._createBubble('is-ai', text, actions);
      loadingEl.parentNode.replaceChild(replacement, loadingEl);
    } else {
      loadingEl.parentNode.removeChild(loadingEl);
    }
    this._scrollToBottom();
  }

  addErrorBubble(message, onRetry) {
    this._hideEmpty();
    const actions = onRetry
      ? [{ label: '↺ Reintentar', primary: false, onClick: onRetry }]
      : [];
    const el = this._createBubble('is-error', `Error: ${message}`, actions);
    this._messages.appendChild(el);
    this._scrollToBottom();
  }

  addStepsBubble(steps, description, onConfirm, onEdit) {
    this._hideEmpty();

    const wrapper = document.createElement('div');
    wrapper.className = 'ai-msg is-ai';

    const bubble = document.createElement('div');
    bubble.className = 'ai-msg__bubble';

    const intro = document.createElement('p');
    intro.className = 'ai-steps-intro';
    intro.textContent = 'Entendí el siguiente proceso. ¿Querés generarlo?';
    bubble.appendChild(intro);

    const ol = document.createElement('ol');
    ol.className = 'ai-steps-list';
    for (const step of steps) {
      const li = document.createElement('li');
      li.textContent = step;
      ol.appendChild(li);
    }
    bubble.appendChild(ol);

    const actionsEl = document.createElement('div');
    actionsEl.className = 'ai-msg__actions';

    const btnConfirm = document.createElement('button');
    btnConfirm.className = 'ai-msg__action-btn is-primary';
    btnConfirm.textContent = '✓ Generar';
    btnConfirm.addEventListener('click', () => {
      btnConfirm.disabled = true;
      btnEdit.disabled    = true;
      onConfirm(description);
    });

    const btnEdit = document.createElement('button');
    btnEdit.className = 'ai-msg__action-btn';
    btnEdit.textContent = '✏️ Editar descripción';
    btnEdit.addEventListener('click', () => {
      btnConfirm.disabled = true;
      btnEdit.disabled    = true;
      onEdit(description);
    });

    actionsEl.appendChild(btnConfirm);
    actionsEl.appendChild(btnEdit);

    wrapper.appendChild(_buildAvatar());
    wrapper.appendChild(_buildBody(bubble, actionsEl, _buildTime()));
    this._messages.appendChild(wrapper);
    this._scrollToBottom();
    return wrapper;
  }

  addIssuesBubble(issues, onHighlight) {
    this._hideEmpty();

    const wrapper = document.createElement('div');
    wrapper.className = 'ai-msg is-ai';

    const bubble = document.createElement('div');
    bubble.className = 'ai-msg__bubble';

    const intro = document.createElement('p');
    intro.className = 'ai-issues-intro';
    const n = issues.length;
    intro.innerHTML = `Se encontraron <strong>${n} problema${n !== 1 ? 's' : ''}</strong>:`;
    bubble.appendChild(intro);

    const ul = document.createElement('ul');
    ul.className = 'ai-issues-list';

    for (const issue of issues) {
      const li = document.createElement('li');
      li.className = 'ai-issue';

      const header = document.createElement('div');
      header.className = 'ai-issue__header';
      header.innerHTML = `⚠ <span class="ai-issue__element">${issue.element_id || 'Diagrama'}</span>`;
      li.appendChild(header);

      const desc = document.createElement('div');
      desc.className = 'ai-issue__desc';
      desc.innerHTML = renderMarkdown(issue.description);
      li.appendChild(desc);

      if (issue.suggestion) {
        const sug = document.createElement('div');
        sug.className = 'ai-issue__suggestion';
        sug.innerHTML = '→ ' + renderMarkdown(issue.suggestion);
        li.appendChild(sug);
      }

      if (issue.element_id && onHighlight) {
        const btn = document.createElement('button');
        btn.className = 'ai-msg__action-btn';
        btn.textContent = '🔍 Ver en canvas';
        btn.addEventListener('click', () => onHighlight(issue.element_id));
        li.appendChild(btn);
      }

      ul.appendChild(li);
    }

    bubble.appendChild(ul);
    wrapper.appendChild(_buildAvatar());
    wrapper.appendChild(_buildBody(bubble, _buildTime()));
    this._messages.appendChild(wrapper);
    this._scrollToBottom();
    return wrapper;
  }

  addSuggestionsBubble(message, suggestions, onSelect) {
    this._hideEmpty();

    const wrapper = document.createElement('div');
    wrapper.className = 'ai-msg is-ai';

    const bubble = document.createElement('div');
    bubble.className = 'ai-msg__bubble';
    if (message) {
      bubble.innerHTML = renderMarkdown(message);
    }

    const optionsEl = document.createElement('div');
    optionsEl.className = 'ai-suggestions';

    for (const suggestion of suggestions) {
      const btn = document.createElement('button');
      btn.className = 'ai-suggestion-btn';
      btn.textContent = suggestion.label;
      if (suggestion.description) btn.title = suggestion.description;

      btn.addEventListener('click', () => {
        optionsEl.querySelectorAll('button').forEach(b => {
          b.disabled = true;
          b.classList.toggle('is-selected', b === btn);
        });
        onSelect(suggestion.value);
      });

      optionsEl.appendChild(btn);
    }

    wrapper.appendChild(_buildAvatar());
    wrapper.appendChild(_buildBody(bubble, optionsEl, _buildTime()));
    this._messages.appendChild(wrapper);
    this._scrollToBottom();
    return wrapper;
  }

  // ── Modal rotating messages ──────────────────────────────────────────────

  /**
   * Starts cycling messages in the modal loading step.
   * @param {'structure'|'generate'} phase
   */
  startModalRotation(phase = 'generate') {
    this.stopModalRotation();
    const el = document.getElementById('ai-gen-loading-text');
    if (!el) return;
    const messages = MODAL_LOADING_MESSAGES[phase] || MODAL_LOADING_MESSAGES.generate;
    let idx = 0;
    el.textContent = messages[0];
    this._modalRotateInterval = setInterval(() => {
      el.classList.add('is-fading');
      setTimeout(() => {
        idx = (idx + 1) % messages.length;
        el.textContent = messages[idx];
        el.classList.remove('is-fading');
      }, 300);
    }, 2500);
  }

  stopModalRotation() {
    if (this._modalRotateInterval) {
      clearInterval(this._modalRotateInterval);
      this._modalRotateInterval = null;
    }
  }

  // ── History (localStorage, per-tab) ──────────────────────────────────────

  saveHistory(messages) {
    try {
      localStorage.setItem(this._histKey(this._activeTabId), JSON.stringify(messages));
    } catch (_) {}
  }

  loadHistory() {
    try {
      const raw = localStorage.getItem(this._histKey(this._activeTabId));
      return raw ? JSON.parse(raw) : [];
    } catch (_) {
      return [];
    }
  }

  clearHistory() {
    try { localStorage.removeItem(this._histKey(this._activeTabId)); } catch (_) {}
    this._messages.innerHTML = '';
    this._messages.appendChild(this._buildEmptyState());
  }

  /**
   * Switch the chat context to a different diagram.
   * Each diagram ID has its own independent tabs and message history.
   * @param {string} diagramId
   */
  switchDiagram(diagramId) {
    this._diagramId = diagramId || 'default';
    this._reloadFromStorage();
  }

  /**
   * PR 1 §4.6 — switch DMN scope (mode + decisionRef) so the panel reads /
   * writes from the storage bucket dedicated to this decision. Pass
   * `mode='bpmn'` (or omit decisionRef) to return to the diagram-level scope.
   * @param {{mode: 'bpmn'|'rules', decisionRef?: string|null, decisionName?: string|null}} opts
   */
  switchDmnScope({ mode = 'bpmn', decisionRef = null, decisionName = null } = {}) {
    const prevMode = this._mode;
    const prevRef  = this._decisionRef;
    this._mode = mode;
    this._decisionRef = (mode === 'rules' && decisionRef) ? decisionRef : null;

    // No-op if scope didn't actually change
    if (prevMode === this._mode && prevRef === this._decisionRef) return;

    this._reloadFromStorage();

    // Surface a system note when entering a decision so the user sees the
    // scope change in the chat itself, not just in the pill.
    if (this._mode === 'rules' && this._decisionRef) {
      const label = decisionName || this._decisionRef;
      this._addSystemNote(`Estás trabajando en la decisión ${label}`);

      // THEMIS_ML_DECISION_PLAN §6.3 — extra welcome note when the active
      // decision uses the ML logic kind. Helps the user remember the
      // governance constraint (fallback REQUIRED) before they hit "simulate".
      const mlInfo = this._detectMlDecisionInfo(this._decisionRef);
      if (mlInfo) {
        const { modelRef, modelVersion } = mlInfo;
        const modelLabel = modelRef
          ? `${modelRef} v${modelVersion || 'latest'}`
          : 'modelo no configurado';
        this._addSystemNote(
          `Estás editando una decisión Machine Learning (modelo \`${modelLabel}\`). ` +
          'Recuerda definir un fallback antes de simular.',
        );
      }
    }
  }

  /**
   * Best-effort lookup of an ML decision's `{ modelRef, modelVersion }` from
   * the active epxDmn model. Returns null when the decision isn't ML.
   * Pure read — no side effects.
   */
  _detectMlDecisionInfo(decisionRef) {
    if (!decisionRef) return null;
    const dmn = (typeof window !== 'undefined') ? window.epxDmn : null;
    if (!dmn) return null;
    try {
      let decision = null;
      if (typeof dmn.getDecisionById === 'function') {
        decision = dmn.getDecisionById(decisionRef);
      }
      if (!decision && typeof dmn.getDefinitions === 'function') {
        const defs = dmn.getDefinitions();
        decision = defs?.drgElement?.find((e) => e && e.id === decisionRef) || null;
      }
      const logic = decision && decision.decisionLogic;
      if (!logic || logic.$type !== 'epx:MachineLearningDecision') return null;
      return {
        modelRef:     logic.modelRef     || '',
        modelVersion: logic.modelVersion || '',
      };
    } catch (_) {
      return null;
    }
  }

  /**
   * Helper used by switchDiagram / switchDmnScope to repaint the panel after
   * the storage bucket changes. Keeps the (de)serialisation logic in one place.
   */
  _reloadFromStorage() {
    this._tabs = this._loadTabIndex();
    if (!this._tabs.length) {
      this._tabs = [{ id: makeTabId(), name: 'Chat 1' }];
      this._saveTabIndex();
    }
    this._activeTabId = this._tabs[0].id;
    this._messages.innerHTML = '';
    const history = this.loadHistory();
    if (history.length > 0) {
      for (const msg of history) {
        if (msg.role === 'user') this.addUserMessage(msg.content);
        else                     this.addAiMessage(msg.content);
      }
    } else {
      this._messages.appendChild(this._buildEmptyState());
    }
    this._renderTabs();
  }

  _addSystemNote(text) {
    if (!this._messages) return;
    this._hideEmpty();
    const wrapper = document.createElement('div');
    wrapper.className = 'ai-msg is-system';
    const bubble = document.createElement('div');
    bubble.className = 'ai-msg__bubble ai-msg__bubble--system';
    bubble.textContent = text;
    wrapper.appendChild(bubble);
    this._messages.appendChild(wrapper);
    this._scrollToBottom();
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  _createBubble(typeClass, text, actions = []) {
    const isAi = typeClass === 'is-ai' || typeClass === 'is-error';
    const wrapper = document.createElement('div');
    wrapper.className = `ai-msg ${typeClass}`;

    const bubble = document.createElement('div');
    bubble.className = 'ai-msg__bubble';
    if (isAi) {
      bubble.innerHTML = renderMarkdown(text);
    } else {
      bubble.textContent = text;
    }

    const actionsEl = document.createElement('div');
    actionsEl.className = 'ai-msg__actions';
    for (const action of actions) {
      const btn = document.createElement('button');
      btn.className = 'ai-msg__action-btn' + (action.primary ? ' is-primary' : '');
      btn.textContent = action.label;
      btn.addEventListener('click', async () => {
        actionsEl.querySelectorAll('button').forEach(b => { b.disabled = true; });
        try {
          await action.onClick();
          btn.classList.add(action.primary ? 'is-applied' : 'is-dismissed');
          actionsEl.querySelectorAll('button').forEach(b => {
            if (b !== btn) b.classList.add('is-dismissed');
          });
        } catch (e) {
          console.error(e);
        }
      });
      actionsEl.appendChild(btn);
    }

    if (isAi) {
      const body = _buildBody(bubble, ...(actions.length ? [actionsEl] : []), _buildTime());
      wrapper.appendChild(_buildAvatar());
      wrapper.appendChild(body);
    } else {
      wrapper.appendChild(bubble);
      if (actions.length) wrapper.appendChild(actionsEl);
      wrapper.appendChild(_buildTime());
    }

    return wrapper;
  }

  _buildEmptyState() {
    const div = document.createElement('div');
    div.className = 'ai-empty-state';
    div.id = 'ai-empty';
    div.innerHTML = '<p>✦</p><p>¿En qué puedo ayudarte?</p>';
    return div;
  }

  _hideEmpty() {
    const empty = document.getElementById('ai-empty');
    if (empty) empty.style.display = 'none';
  }

  _scrollToBottom() {
    requestAnimationFrame(() => {
      this._messages.scrollTop = this._messages.scrollHeight;
    });
  }
}
